import { useState } from "react";
import { useAccount } from "@/hooks/useAccount";
import { fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Download, FileSpreadsheet, FileBarChart, FileCheck2, CheckCircle2, Clock, FileSignature } from "lucide-react";
import { toast } from "sonner";

const DOC_ICONS = { "Tax Document": FileSpreadsheet, Report: FileBarChart, Statement: FileText, KYC: FileCheck2, Agreement: FileSignature };
const TABS = [
  { key: "documents", label: "Documents" },
  { key: "agreements", label: "Agreements" },
  { key: "approvals", label: "Approvals" },
];

function StatusBadge({ status }) {
  const ok = ["Signed", "Approved", "Verified", "Active"].includes(status);
  return (
    <Badge className={`${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"} border-0 font-medium gap-1`}>
      {ok ? <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> : <Clock className="w-3 h-3" strokeWidth={2} />}
      {status}
    </Badge>
  );
}

export default function Documents() {
  const { data, isLoading } = useAccount();
  const [tab, setTab] = useState("documents");

  if (isLoading) {
    return <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>;
  }

  const docs = data?.documents || [];
  const agreements = data?.agreements || [];
  const approvals = data?.approvals || [];

  const openDoc = (d) => {
    if (d.url) { window.open(d.url, "_blank", "noopener"); }
    else { toast.success(`Preparing "${d.name}"…`); }
  };

  return (
    <div className="space-y-8" data-testid="documents-page">
      <div className="fade-up">
        <p className="overline mb-1">Compliance &amp; Library</p>
        <h1 className="font-serif text-4xl font-bold text-slate-900">Documents, Agreements &amp; Approvals</h1>
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1 w-fit fade-up">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`docs-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors duration-200 ${
              tab === t.key ? "bg-[#15803d] text-white" : "text-slate-500 hover:text-slate-800"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "documents" && (
        docs.length === 0 ? (
          <Card className="p-12 text-center bg-white border-slate-200 text-slate-500">No documents available yet.</Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {docs.map((d, i) => {
              const Icon = DOC_ICONS[d.type] || FileText;
              return (
                <Card key={d.id || i} data-testid={`doc-card-${i}`}
                  className="p-6 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] hover:shadow-[0_8px_24px_-4px_rgba(15,23,42,0.12)] hover:-translate-y-0.5 transition-transform duration-300 fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-11 h-11 rounded-2xl bg-emerald-50 flex items-center justify-center text-[#15803d]">
                      <Icon className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <span className="text-xs text-slate-400 tabular-nums">{fmtDate(d.date)}</span>
                  </div>
                  <p className="overline mb-1">{d.type}</p>
                  <h3 className="font-serif text-xl font-bold text-slate-900 leading-snug mb-4">{d.name}</h3>
                  <Button variant="outline" size="sm" data-testid={`download-doc-${i}`} onClick={() => openDoc(d)}
                    className="rounded-full border-slate-300 hover:bg-slate-50 transition-colors duration-200">
                    <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} /> {d.url ? "View / Download" : "Download"}
                  </Button>
                </Card>
              );
            })}
          </div>
        )
      )}

      {(tab === "agreements" || tab === "approvals") && (
        (tab === "agreements" ? agreements : approvals).length === 0 ? (
          <Card className="p-12 text-center bg-white border-slate-200 text-slate-500">Nothing here yet.</Card>
        ) : (
          <div className="space-y-3">
            {(tab === "agreements" ? agreements : approvals).map((a, i) => (
              <Card key={a.id || i} data-testid={`${tab}-row-${i}`}
                className="p-5 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] flex items-center justify-between gap-4 fade-up"
                style={{ animationDelay: `${i * 50}ms` }}>
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-11 h-11 rounded-2xl bg-emerald-50 flex items-center justify-center text-[#15803d] shrink-0">
                    {tab === "agreements" ? <FileSignature className="w-5 h-5" strokeWidth={1.75} /> : <FileCheck2 className="w-5 h-5" strokeWidth={1.75} />}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{a.name}</p>
                    <p className="text-sm text-slate-400">{fmtDate(a.date)}</p>
                  </div>
                </div>
                <StatusBadge status={a.status} />
              </Card>
            ))}
          </div>
        )
      )}
    </div>
  );
}
